  (() => {
    const AUTOPLAY_MS = 5000;
    const REVIEW_AUTOPLAY_MS = 6000;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function initHeroSlider(root) {
      if (!root || root.dataset.ppHeroReady === 'true') return;

      const slides = Array.from(root.querySelectorAll('[data-pp-hero-slide]'));
      const dots = Array.from(root.querySelectorAll('[data-pp-hero-dot]'));
      const current = root.querySelector('[data-pp-hero-current]');
      const swipeSurface = root.querySelector('.pp-front-hero__slides') || root;
      const videos = slides.map((slide) => slide.querySelector('video'));

      if (slides.length < 2 || dots.length !== slides.length) return;

      root.dataset.ppHeroReady = 'true';
      let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer = null;
      let pointerId = null;
      let startX = 0;
      let startY = 0;
      let preventClick = false;
      let isInViewport = !('IntersectionObserver' in window);
      let isPointerInside = false;
      let hasFocus = root.contains(document.activeElement);
      let destroyed = false;
      let heroObserver = null;
      const listeners = [];

      function listen(target, type, listener, options) {
        target.addEventListener(type, listener, options);
        listeners.push(() => target.removeEventListener(type, listener, options));
      }

      function isVisible() {
        return !destroyed && !document.hidden && isInViewport;
      }

      function syncVideos(resetActiveVideo = false) {
        videos.forEach((video, videoIndex) => {
          if (!video) return;

          if (videoIndex === activeIndex) {
            video.muted = true;
            video.defaultMuted = true;
            video.playsInline = true;
            if (resetActiveVideo) video.currentTime = 0;
            if (isVisible()) {
              video.play()?.catch(() => {});
            } else {
              video.pause();
            }
          } else {
            video.pause();
            video.currentTime = 0;
          }
        });
      }

      function getActiveDelay() {
        const slide = slides[activeIndex];
        const slideDelay = Number(slide?.dataset.ppHeroDuration);
        const video = videos[activeIndex];

        if (Number.isFinite(slideDelay) && slideDelay > 0) return slideDelay;
        if (video && Number.isFinite(video.duration) && video.duration > 0) return video.duration * 1000;

        return AUTOPLAY_MS;
      }

      function setSlide(index) {
        if (destroyed) return;
        const previousIndex = activeIndex;
        activeIndex = (index + slides.length) % slides.length;

        slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === activeIndex;
          slide.classList.toggle('is-active', isActive);
          slide.toggleAttribute('inert', !isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        dots.forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === activeIndex);
          dot.setAttribute('aria-pressed', dotIndex === activeIndex ? 'true' : 'false');
        });

        if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
        syncVideos(previousIndex !== activeIndex);
      }

      function stop() {
        if (!timer) return;
        window.clearTimeout(timer);
        timer = null;
      }

      function start() {
        stop();
        syncVideos();
        if (!isVisible() || reducedMotion.matches || isPointerInside || hasFocus || pointerId !== null) return;
        timer = window.setTimeout(() => {
          setSlide(activeIndex + 1);
          start();
        }, getActiveDelay());
      }

      dots.forEach((dot, index) => {
        listen(dot, 'click', () => {
          setSlide(index);
          start();
        });
      });

      root.querySelectorAll('.pp-front-hero__banner-link').forEach((link) => {
        listen(link, 'click', (event) => {
          if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) return;

          const href = link.getAttribute('href');
          if (!href || href === '#') return;

          window.location.href = href;
        });
      });

      listen(swipeSurface, 'pointerdown', (event) => {
        if (event.button !== undefined && event.button !== 0) return;
        if (event.pointerType === 'mouse' && event.target.closest('.pp-front-hero__banner-link')) return;

        pointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
        preventClick = false;
        swipeSurface.classList.add('is-dragging');
        swipeSurface.setPointerCapture?.(event.pointerId);
        stop();
      });

      listen(swipeSurface, 'pointermove', (event) => {
        if (pointerId !== event.pointerId) return;

        const moveX = event.clientX - startX;
        const moveY = event.clientY - startY;

        if (Math.abs(moveX) > 12 && Math.abs(moveX) > Math.abs(moveY)) {
          preventClick = true;
          event.preventDefault();
        }
      });

      function finishSwipe(event) {
        if (pointerId !== event.pointerId) return;

        const moveX = event.clientX - startX;
        const moveY = event.clientY - startY;
        const isSwipe = Math.abs(moveX) > 44 && Math.abs(moveX) > Math.abs(moveY) * 1.2;

        swipeSurface.classList.remove('is-dragging');
        swipeSurface.releasePointerCapture?.(event.pointerId);
        pointerId = null;

        if (isSwipe) {
          setSlide(activeIndex + (moveX < 0 ? 1 : -1));
        }

        start();
      }

      listen(swipeSurface, 'pointerup', finishSwipe);
      listen(swipeSurface, 'pointercancel', finishSwipe);
      listen(swipeSurface, 'click', (event) => {
        if (!preventClick) return;
        event.preventDefault();
        event.stopPropagation();
        preventClick = false;
      }, true);

      listen(root, 'mouseenter', () => {
        isPointerInside = true;
        stop();
      });
      listen(root, 'mouseleave', () => {
        isPointerInside = false;
        start();
      });
      listen(root, 'focusin', () => {
        hasFocus = true;
        stop();
      });
      listen(root, 'focusout', (event) => {
        hasFocus = Boolean(event.relatedTarget && root.contains(event.relatedTarget));
        start();
      });
      listen(document, 'visibilitychange', start);
      listen(reducedMotion, 'change', start);

      if ('IntersectionObserver' in window) {
        heroObserver = new IntersectionObserver(([entry]) => {
          if (destroyed) return;
          isInViewport = Boolean(entry?.isIntersecting);
          start();
        }, { threshold: 0 });
        heroObserver.observe(root);
      }

      function handleSectionUnload(event) {
        if (event.target !== root && !event.target?.contains?.(root)) return;
        destroyed = true;
        stop();
        syncVideos();
        heroObserver?.disconnect();
        listeners.forEach((removeListener) => removeListener());
        delete root.dataset.ppHeroReady;
      }

      listen(document, 'shopify:section:unload', handleSectionUnload);

      setSlide(activeIndex);
      start();
    }

    function initAllHeroSliders(event) {
      const scope = event?.target || document;
      scope.querySelectorAll('[data-pp-hero-slider]').forEach(initHeroSlider);
    }

    function initReviewSlider(root) {
      if (!root || root.dataset.ppReviewReady === 'true') return;

      const rail = root.querySelector('[data-pp-review-rail]');
      const prev = root.querySelector('[data-pp-review-prev]');
      const next = root.querySelector('[data-pp-review-next]');
      const dotsContainer = root.querySelector('[data-pp-review-dots]');
      const filters = Array.from(root.querySelectorAll('[data-pp-review-filter]'));
      const cards = Array.from(root.querySelectorAll('.pp-front-review-card'));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let currentPage = 0;
      let dots = [];
      let itemsPerPage = 1;
      let firstVisibleCardIndex = 0;
      let autoplayTimer = null;
      let isPointerInside = false;
      let isInViewport = !('IntersectionObserver' in window);
      let reviewObserver = null;

      if (!rail || !prev || !next || !dotsContainer || cards.length === 0) return;

      root.dataset.ppReviewReady = 'true';

      function getVisibleCards() {
        return cards.filter((card) => !card.classList.contains('is-hidden'));
      }

      function getStep() {
        const firstCard = getVisibleCards()[0];
        if (!firstCard) return rail.clientWidth * 0.85;

        const styles = window.getComputedStyle(rail);
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return firstCard.getBoundingClientRect().width + gap;
      }

      function getItemsPerPage() {
        const step = getStep();
        if (step <= 0) return 1;

        return Math.max(1, Math.round(rail.clientWidth / step));
      }

      function getMaxOffset() {
        return Math.max(0, rail.scrollWidth - rail.clientWidth);
      }

      function stopAutoplay() {
        if (!autoplayTimer) return;
        window.clearTimeout(autoplayTimer);
        autoplayTimer = null;
      }

      function startAutoplay() {
        stopAutoplay();

        if (
          reducedMotion.matches ||
          document.hidden ||
          !isInViewport ||
          isPointerInside ||
          root.contains(document.activeElement) ||
          dots.length <= 1
        ) return;

        autoplayTimer = window.setTimeout(() => {
          const nextPage = currentPage >= dots.length - 1 ? 0 : currentPage + 1;
          setReviewPage(nextPage);
          startAutoplay();
        }, REVIEW_AUTOPLAY_MS);
      }

      function setReviewPage(index, behavior = 'smooth') {
        const maxPage = Math.max(0, dots.length - 1);
        const shouldJump = behavior === 'auto' || reducedMotion.matches;
        currentPage = Math.max(0, Math.min(index, maxPage));

        const step = getStep();
        const requestedOffset = step * itemsPerPage * currentPage;
        const offset = Math.min(requestedOffset, getMaxOffset());
        firstVisibleCardIndex = step > 0 ? Math.round(offset / step) : 0;

        if (shouldJump) {
          rail.style.transition = 'none';
        }

        rail.style.setProperty('--pp-review-offset', `${-offset}px`);
        updateButtons();

        if (shouldJump) {
          window.requestAnimationFrame(() => {
            rail.style.transition = '';
          });
        }
      }

      function renderDots() {
        dotsContainer.innerHTML = '';
        const visibleCards = getVisibleCards();
        itemsPerPage = getItemsPerPage();
        const pageCount = Math.max(1, Math.ceil(visibleCards.length / itemsPerPage));
        dots = Array.from({ length: pageCount }, (_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.dataset.ppReviewDot = '';
          dot.setAttribute('aria-label', `Show review page ${index + 1}`);
          dot.setAttribute('aria-pressed', 'false');
          dot.addEventListener('click', () => {
            setReviewPage(index);
            startAutoplay();
          });
          dotsContainer.appendChild(dot);
          return dot;
        });
      }

      function updateCardAccessibility() {
        const visibleCards = getVisibleCards();
        const firstVisibleIndex = firstVisibleCardIndex;
        const lastVisibleIndex = firstVisibleIndex + itemsPerPage;

        cards.forEach((card) => {
          const visibleIndex = visibleCards.indexOf(card);
          const isOnCurrentPage = visibleIndex >= firstVisibleIndex && visibleIndex < lastVisibleIndex;

          card.toggleAttribute('inert', !isOnCurrentPage);
          card.setAttribute('aria-hidden', isOnCurrentPage ? 'false' : 'true');
        });
      }

      function updateButtons() {
        prev.disabled = currentPage <= 0 || dots.length <= 1;
        next.disabled = currentPage >= dots.length - 1 || dots.length <= 1;
        dots.forEach((dot, dotIndex) => {
          const isActive = dotIndex === currentPage;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-pressed', String(isActive));
        });
        updateCardAccessibility();
      }

      function applyFilter(filterValue) {
        cards.forEach((card) => {
          const showCard = filterValue === 'all' || card.dataset.product === filterValue;
          card.classList.toggle('is-hidden', !showCard);
        });

        renderDots();
        setReviewPage(0, 'auto');
        startAutoplay();
      }

      prev.addEventListener('click', () => {
        setReviewPage(currentPage - 1);
        startAutoplay();
      });

      next.addEventListener('click', () => {
        setReviewPage(currentPage + 1);
        startAutoplay();
      });

      filters.forEach((filter) => {
        filter.addEventListener('click', () => {
          filters.forEach((item) => {
            const isActive = item === filter;
            item.classList.toggle('is-active', isActive);
            item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          });
          applyFilter(filter.dataset.ppReviewFilter || 'all');
        });
      });

      function handleResize() {
        renderDots();
        setReviewPage(currentPage, 'auto');
        startAutoplay();
      }

      function handleVisibilityChange() {
        if (document.hidden) {
          stopAutoplay();
        } else {
          startAutoplay();
        }
      }

      root.addEventListener('mouseenter', () => {
        isPointerInside = true;
        stopAutoplay();
      });
      root.addEventListener('mouseleave', () => {
        isPointerInside = false;
        startAutoplay();
      });
      root.addEventListener('focusin', stopAutoplay);
      root.addEventListener('focusout', (event) => {
        if (event.relatedTarget && root.contains(event.relatedTarget)) return;
        window.requestAnimationFrame(startAutoplay);
      });
      document.addEventListener('visibilitychange', handleVisibilityChange);
      reducedMotion.addEventListener('change', startAutoplay);
      window.addEventListener('resize', handleResize);

      if ('IntersectionObserver' in window) {
        reviewObserver = new IntersectionObserver(([entry]) => {
          isInViewport = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.15);
          if (isInViewport) {
            startAutoplay();
          } else {
            stopAutoplay();
          }
        }, { threshold: [0, 0.15] });
        reviewObserver.observe(root);
      }

      function handleSectionUnload(event) {
        if (event.target !== root && !event.target?.contains?.(root)) return;
        stopAutoplay();
        reviewObserver?.disconnect();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        document.removeEventListener('shopify:section:unload', handleSectionUnload);
        reducedMotion.removeEventListener('change', startAutoplay);
        window.removeEventListener('resize', handleResize);
      }

      document.addEventListener('shopify:section:unload', handleSectionUnload);

      const activeFilter = filters.find((filter) => filter.classList.contains('is-active'));
      applyFilter(activeFilter?.dataset.ppReviewFilter || 'all');
    }

    function initAllReviewSliders(event) {
      const scope = event?.target || document;
      scope.querySelectorAll('[data-pp-review-slider]').forEach(initReviewSlider);
    }

    initAllHeroSliders();
    initAllReviewSliders();
    document.addEventListener('shopify:section:load', initAllHeroSliders);
    document.addEventListener('shopify:section:load', initAllReviewSliders);
  })();
